/**
 * ErrorBoundary — DysTest
 *
 * React class component that catches runtime errors anywhere in its child
 * tree and renders a graceful fallback instead of an empty white screen.
 *
 * Two variants:
 *   <ErrorBoundary>          — full-page fallback (used in App.tsx)
 *   <ErrorBoundary inline>   — compact inline fallback (individual sections)
 *
 * React DOM failures must surface: browser translation can rewrite React-owned
 * nodes. Suppressing these as WebGazer noise silently remounts the page.
 *
 * A stale JS chunk after a new deployment (Vite: "Failed to fetch dynamically
 * imported module", webpack: ChunkLoadError) is auto-recovered:
 * "Try again" can never fix it — it just re-requests the same now-missing
 * chunk URL — so a normal reload (which fetches the current chunk manifest)
 * is triggered automatically, once, instead of leaving the user stuck on a
 * dead-end button mid-assessment.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { captureException } from '../services/errorReporter';

interface Props {
  children: ReactNode;
  /** Show a compact inline fallback instead of full-page */
  inline?: boolean;
  /** Custom fallback element */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** True while an automatic reload (chunk-error recovery) is in flight */
  isAutoReloading: boolean;
}

const isDomMutationError = (err: Error) =>
  /removeChild|insertBefore/.test(err.message) && /not a child|not.*child of this node/i.test(err.message);

// Thrown by dynamic import() when a lazy-loaded route's JS chunk 404s —
// almost always because a new deployment replaced the chunk files while this
// tab still has the old version open. Vite (the current build) words it per
// browser; the webpack wording is kept for older cached builds.
const CHUNK_LOAD_MESSAGES = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge (Vite)
  /error loading dynamically imported module/i, // Firefox (Vite)
  /Importing a module script failed/i, // Safari (Vite)
  /Unable to preload CSS/i, // Vite CSS preload
  /Loading (chunk|CSS chunk) [\w.-]+ failed/i, // webpack
];
export const isChunkLoadError = (err: Error) =>
  err.name === 'ChunkLoadError' || CHUNK_LOAD_MESSAGES.some((re) => re.test(err.message));

export const CHUNK_RELOAD_KEY = 'dystest_chunk_reload_attempted';
// How long a fresh mount must stay error-free before we consider the page
// "healthy" again and re-arm auto-reload for a future, unrelated incident.
const CHUNK_RELOAD_REARM_MS = 8000;

class ErrorBoundary extends Component<Props, State> {
  private rearmTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isAutoReloading: false };
  }

  override componentDidMount() {
    // Re-arm auto-reload after this mount has been stable for a while, so a
    // later, unrelated chunk-load incident (e.g. a different deploy, much
    // later in a long assessment session) also gets its own reload attempt.
    this.rearmTimer = setTimeout(() => {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    }, CHUNK_RELOAD_REARM_MS);
  }

  override componentWillUnmount() {
    if (this.rearmTimer) clearTimeout(this.rearmTimer);
  }

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      return { hasError: true, error, isAutoReloading: true };
    }
    return { hasError: true, error, isAutoReloading: false };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        console.warn('[ErrorBoundary] Stale chunk after a new deployment — auto-reloading once.');
        window.location.reload();
        return; // about to reload — no point reporting this one
      }
      // Already auto-reloaded once and the chunk error recurred — a real
      // problem (offline, server down), not just a stale manifest. Fall
      // through to report it and show the manual-recovery UI.
    }
    captureException(error, { componentStack: info.componentStack ?? '' });
    if (process.env['NODE_ENV'] !== 'production') {
      console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, isAutoReloading: false });
  };

  override render() {
    if (!this.state.hasError) return this.props.children;

    // Auto-reload in flight (stale chunk) — show a calm, non-alarming notice
    // instead of "Something went wrong" while the reload completes.
    if (this.state.isAutoReloading) {
      return (
        <div
          role="status"
          aria-live="polite"
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 24px',
            background: '#fff',
            fontFamily: 'system-ui, sans-serif',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 36, height: 36, borderRadius: '50%',
              border: '3px solid #e0e7ff', borderTopColor: '#6366f1',
              animation: 'spin 0.7s linear infinite', marginBottom: 20,
            }}
          />
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
          <p style={{ fontSize: 15, color: '#64748b' }}>Updating to the latest version…</p>
        </div>
      );
    }

    // Custom fallback provided by caller
    if (this.props.fallback) return this.props.fallback;

    // ── Compact inline fallback ──────────────────────────────────────────────
    if (this.props.inline) {
      return (
        <div
          role="alert"
          style={{
            padding: '16px 20px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 12,
            color: '#991b1b',
            fontSize: 14,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <span style={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true">⚠️</span>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>Something went wrong here.</strong>
            <button
              onClick={this.handleReset}
              style={{
                marginTop: 8,
                fontSize: 13,
                color: '#b91c1c',
                background: 'none',
                border: '1px solid #fca5a5',
                borderRadius: 6,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    // ── Full-page fallback ───────────────────────────────────────────────────
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          background: '#fff',
          fontFamily: 'system-ui, sans-serif',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 24 }} aria-hidden="true">😟</div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: 16, color: '#64748b', maxWidth: 440, lineHeight: 1.6, marginBottom: 32 }}>
          {this.state.error && isDomMutationError(this.state.error)
            ? 'The page could not be updated. If browser translation is enabled, choose “Show original” and reload. An action already accepted by the server may have completed; check its status before trying again.'
            : 'An unexpected error interrupted the page. You can try again — if the problem persists, please reload the page.'}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={this.handleReset}
            style={{
              padding: '12px 28px',
              background: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 28px',
              background: 'none',
              color: '#64748b',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>

        {process.env['NODE_ENV'] === 'development' && this.state.error && (
          <details
            style={{
              marginTop: 40,
              textAlign: 'left',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '12px 16px',
              maxWidth: 640,
              fontSize: 12,
              color: '#475569',
              fontFamily: 'monospace',
            }}
          >
            <summary style={{ cursor: 'pointer', marginBottom: 8, fontFamily: 'system-ui', fontSize: 13 }}>
              Error details (dev only)
            </summary>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {this.state.error.toString()}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
